import { lazy, Suspense, type ReactNode } from "react";
import { Route, Routes } from "react-router-dom";
import CircularLoading from "@/components/circular-loading";
import DesktopSidebar, { SIDEBAR_TRANSITION } from "@/components/desktop-sidebar";
import { useSettingStore } from "@/store/setting-store";
import { useDesktopChrome } from "@/layouts/responsive-routes";
const GlobalAudioBar = lazy(() => import("@/features/audio-player/global-audio-bar"));
import ScrollToTop from "@/components/scroll-to-top";
import Toaster from "@/components/toaster";
import { AuthProvider } from "@/providers/auth-provider";
import { MiniappProvider } from "@/providers/mini-app-provider";
import { WriteAccessProvider } from "@/providers/write-access-provider";
import { AnalyticsProvider } from "@/features/analytics/analytics-provider";

import ReactQueryProvider from "@/providers/react-query-provider";
import NavigationLayout from "@/layouts/navigation-layout";

const HomePage = lazy(() => import("@/pages/home-page"));
import MyCoursesPage from "@/pages/my-courses-page";
import ProfilePage from "@/pages/profile-page";
import SearchPage from "@/pages/search-page";
import CategoryPage from "@/pages/category-page";
const CoursePage = lazy(() => import("@/pages/course-page"));
const EducatorPage = lazy(() => import("@/pages/educator-page"));
const CoursePurchasePage = lazy(() => import("@/pages/course-purchase-page"));
import CourseLessonPage from "@/pages/course-lesson-page";
const QuizPage = lazy(() => import("@/pages/quiz-page"));
const PlatformCertificatePage = lazy(() => import("@/pages/platform-certificate-page"));
const PlatformCertificateExamPage = lazy(() => import("@/pages/platform-certificate-exam-page"));
const PlatformCertificateRedirectPage = lazy(() => import("@/pages/platform-certificate-redirect-page"));
const CertificateVerifyPage = lazy(() => import("@/pages/certificate-verify-page"));
import MiniAppPage from "@/pages/miniapp-page";
import PaymentPage from "@/pages/payment-page";
import PaymentCallbackPage from "@/pages/payment-callback-page";
import SettingsPage from "@/pages/settings-page";
import TermOfConditionsPage from "@/pages/term-of-conditions-page";
import DeviceInfoPage from "@/pages/device-info-page";
import WishlistPage from "@/pages/wishlist-page";
import ProfileEditPage from "@/pages/profile-edit-page";
import LoginPage from "@/pages/login-page";
import TransactionsPage from "@/pages/transactions-page";
import EducatorsPage from "@/pages/educators";
import SupportPage from "@/pages/support-page";


/**
 * Owns the desktop sidebar and the page width.
 *
 * The two are nested, not combined: the outer box clears the fixed sidebar,
 * and the width cap sits inside it so a not-yet-responsive page centres in
 * whatever space is left rather than being squeezed by the padding.
 *
 * Lives here rather than in NavigationLayout because the sidebar links to
 * pages outside that layout (wishlist, settings).
 */
function AppShell({ children }: { children: ReactNode }) {
  const { hasSidebar, container } = useDesktopChrome();
  const collapsed = useSettingStore((s) => s.sidebarCollapsed);

  // Sidebar inset + width + a matching gap. Keep in step with
  // SIDEBAR_EXPANDED / SIDEBAR_COLLAPSED in desktop-sidebar.tsx.
  const sidebarClearance = !hasSidebar
    ? ""
    : collapsed
      ? "lg:pr-[100px]"
      : "lg:pr-[288px]";

  return (
    <>
      {hasSidebar && <DesktopSidebar />}
      <div
        className={`transition-[padding] ${SIDEBAR_TRANSITION} ${sidebarClearance}`}
      >
        {/* Desktop routes get the rail their content calls for; everything
            else keeps the phone cap, so pages never set their own width. */}
        <div className={container ?? "container mx-auto w-full max-w-xl"}>
          {children}
        </div>
      </div>
    </>
  );
}

export default function App() {
  return (
    <MiniappProvider>
      <ReactQueryProvider>
        <AuthProvider>
          {/* Inside AuthProvider so the prompt lands after the session is
              settled rather than during the init-data round-trip. */}
          <WriteAccessProvider>
          <AnalyticsProvider>
          <ScrollToTop />
          <Toaster />
          <GlobalAudioBar />
          <AppShell>
          <Suspense fallback={<CircularLoading />}>
            <Routes>
              <Route element={<NavigationLayout />}>
                <Route index element={<HomePage />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="my-courses" element={<MyCoursesPage />} />
                <Route path="profile" element={<ProfilePage />} />
              </Route>
              
              <Route path="course/:courseId" element={<CoursePage />} />
              <Route path="course/:courseId/buy" element={<CoursePurchasePage />} />
              <Route path="course/:courseId/lesson/:lessonId" element={<CourseLessonPage />} />
              <Route path="course/:courseId/lesson/:lessonId/quiz" element={<QuizPage />} />
              <Route path="course/:courseId/certificate" element={<PlatformCertificateRedirectPage />} />
              <Route path="course/:courseId/certificate/:offeringId" element={<PlatformCertificatePage />} />
              <Route
                path="course/:courseId/certificate/:offeringId/exam"
                element={<PlatformCertificateExamPage />}
              />
              {/* Public — where the QR printed on every certificate points. */}
              <Route path="certificate" element={<CertificateVerifyPage />} />
              <Route path="certificate/:code" element={<CertificateVerifyPage />} />
              <Route path="categories/:categoryId" element={<CategoryPage />} />
              <Route path="miniapp/:miniappId" element={<MiniAppPage />} />
              <Route path="educator/:educatorId" element={<EducatorPage />} />
              <Route path="payment" element={<PaymentPage />} />
              <Route
                path="api/payments/callback/local"
                element={<PaymentCallbackPage />}
              />
              <Route path="login" element={<LoginPage />} />
              <Route path="profile/edit" element={<ProfileEditPage />} />
              <Route path="profile/wishlist" element={<WishlistPage />} />
              <Route path="profile/transactions" element={<TransactionsPage />} />
              <Route path="profile/settings" element={<SettingsPage />} />
              <Route
                path="profile/term-of-conditions"
                element={<TermOfConditionsPage />}
              />
              <Route path="profile/device-info" element={<DeviceInfoPage />} />
              <Route path="profile/support" element={<SupportPage />} />
              <Route path="educators" element={<EducatorsPage />} />
            </Routes>
          </Suspense>
          </AppShell>
          </AnalyticsProvider>
          </WriteAccessProvider>
        </AuthProvider>
      </ReactQueryProvider>
    </MiniappProvider>
  );
}
